import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CoreService } from '../../../services/core.service';
import { Subject } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { Router } from "@angular/router";
import { ChangeDetectionStrategy, Component as NgComponent, Inject } from '@angular/core';
import { MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';

@NgComponent({
  selector: 'app-delete-confirm-dialog',
  standalone: true,
  template: `
    <h2 mat-dialog-title class="text-red-50">Confirm Delete</h2>
    <mat-dialog-content>
      Are you sure you want to delete post with title: <b>{{ data.rss_title }}</b>?
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-button color="warn" [mat-dialog-close]="'confirm'">Delete</button>
    </mat-dialog-actions>
  `,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, MatButtonModule, CommonModule, FormsModule, MatIconModule, MatButtonModule, MatTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class DeleteConfirmDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: { rss_title: string }) { }
}
type Post = {
  id: number; rss_title: string; domain_name?: string; image_url: string; post_status?: string; enable_repeat?: number; parent_post_id?: number; tag?: string; social_platform_title: string; scheduled_for?: string | Date; rss_description: string; logo_file_path: string; thumbnail_file_path: string; text_direction?: 'ltr' | 'rtl';
};
@Component({
  selector: 'grid-based-layout',
  standalone: true,
  imports: [CommonModule, FormsModule, MatPaginatorModule, MatIconModule, MatButtonModule, MatTooltip],
  templateUrl: './grid-based-layout.component.html',
  styleUrls: ['./grid-based-layout.component.scss'],
})
export class GridBasedLayoutComponent implements OnInit {
  // state
  companyID = 0;
  searchValue = '';
  search$ = new Subject<string>();

  // data
  posts: Post[] = [];
  @ViewChildren(MatPaginator) paginators!: QueryList<MatPaginator>;
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;

  // localStorage key
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private isPublished(post: Post): boolean {
    const status = (post.post_status || '').toLowerCase();

    return (
      status === 'postedoninstagram'.toLowerCase() ||
      status === 'postedonfacebook'.toLowerCase() ||
      status === 'postedontiktok'.toLowerCase()
    );
  }

  // ui
  isLoading = false;
  errorMsg = '';

  constructor(private coreService: CoreService, private router: Router, private dialog: MatDialog, private route: ActivatedRoute,
  ) { }

  // ngOnInit(): void {
  //   this.companyID = this.coreService.user?.companyID || 0;
  //    this.search$
  //   .pipe(
  //     debounceTime(300),            
  //     distinctUntilChanged(),       
  //     filter(value => value.length >= 2 || value.length === 0) 
  //   )
  //   .subscribe(value => {
  //     this.searchValue = value;
  //     this.loadData(1);
  //   });
  //    this.route.queryParams.subscribe(params => {
  //     const page = params['page'] ? Number(params['page']) : 1;
  //     this.loadData(page);
  //   });

  // this.loadData(1);
  // }

  ngOnInit(): void {
    this.companyID = this.coreService.user?.companyID || 0;

    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(value => value.length >= 2 || value.length === 0)
      )
      .subscribe(value => {
        this.searchValue = value;
        this.loadData(1);
      });

    this.route.queryParams.subscribe(params => {
      const page = params['page'] ? Number(params['page']) : 1;
      this.loadData(page);
    });

    // Removed the direct this.loadData(1) call
  }

  loadData(page: number): void {
    this.isLoading = true;
    this.errorMsg = '';

    this.coreService
      .postData('posts/index', {
        company_id: this.companyID,
        page,
        q: this.searchValue || '',
        per_page: this.pageSize,   // backend may use any of these
        limit: this.pageSize,
        page_size: this.pageSize,
      })
      .subscribe({
        next: (res: any) => {
          const incoming: Post[] = res?.data ?? [];
          this.posts = incoming;

          this.totalItems = Number(res?.total) || 0;
          this.pageSize = Number(res?.per_page) || this.pageSize;
          this.currentPage = Number(res?.current_page) || page;

          setTimeout(() => this.syncPaginators());

          if (this.currentPage === 1) {
            this.setLatestPosts();
          }
        },
        error: (err: any) => {
          console.error('Posts load error:', err);
          this.errorMsg = 'Could not load posts.';
          this.posts = [];
          this.totalItems = 0;

          setTimeout(() => {
            this.paginators?.forEach(p => {
              p.length = 0;
              p.pageIndex = 0;
            });
          });
        },
        complete: () => (this.isLoading = false),
      });
  }


  get totalPages(): number {
    const per = this.pageSize || 1;
    const total = this.totalItems || 0;
    return Math.max(1, Math.ceil(total / per));
  }

  editRow(row: Post) {
    // disabled state is handled in template; just navigate
    this.router.navigate(['/edit-post', row.id]);
  }
  repeatPostRow(row: any) {
    this.router.navigate(["/repeat-post", row.id]);
  }
  reuseRow(row: Post) {
    const targetId = row.enable_repeat === -1 ? (row.parent_post_id ?? row.id) : row.id;
    this.router.navigate(['/reuse-post', targetId]);
  }

  viewRow(row: Post) {
    const targetId = row.enable_repeat === -1 ? (row.parent_post_id ?? row.id) : row.id;
    this.router.navigate(['/preview-post', row.tag, targetId], {
      queryParams: { returnPage: this.currentPage } // ← add this
    });
  }

  openDeleteConfirm(row: Post) {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '380px',
      data: { rss_title: row.rss_title }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        this.deleteRow(row);
      }
    });
  }

  private deleteRow(row: Post) {
    this.coreService.postData(`manual-post/deletePost/${row.id}`, {
      company_id: this.companyID
    }).subscribe({
      next: () => this.loadData(1),
      error: (err) => {
        console.error('Delete failed:', err);
        this.errorMsg = 'Could not delete post.';
      }
    });
  }

  submitSearch(value: string): void {
    if (value && value.length < 2) return;
    this.search$.next(value ?? '');
  }


  private isSyncing = false;

  onPageChange(event: any) {
    if (this.isSyncing) return;
    const newPage = event.pageIndex + 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: newPage },
      queryParamsHandling: 'merge'
    });
  }

  private syncPaginators(): void {
    this.isSyncing = true;
    this.paginators?.forEach(p => {
      p.length = this.totalItems;
      p.pageSize = this.pageSize;
      p.pageIndex = Math.max(0, this.currentPage - 1);
    });
    // Let Angular process the changes before releasing the guard
    setTimeout(() => this.isSyncing = false);
  }
  
  platformTextClass(plat?: string) {
    const t = (plat || '').toLowerCase().trim();
    switch (t) {
      case 'instagram': return 'text-fuchsia-600';
      case 'facebook': return 'text-blue-600';
      case 'x': return 'text-neutral-700';
      case 'youtube': return 'text-red-600';
      default: return 'text-gray-700';
    }
  }
  // avopolis.gr -> "avopolis"
  domainSlug(domain?: string): string | null {
    if (!domain) return null;
    const host = String(domain).toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split(/[\/:?#]/)[0];
    return host.split('.')[0].replace(/[^a-z0-9]/g, '') || null;
  }


  naPlaceholder = './assets/images/na.png';
  videoPlaceholder = './assets/images/video.png';

  private isYouTubeUrl(url: string): boolean {
    return /(^https?:\/\/)?(www\.)?(youtube\.com|m\.youtube\.com|youtu\.be)\//i.test(url || '');
  }

  private getYouTubeId(url: string): string | null {
    const u = (url || '').trim();

    // https://www.youtube.com/embed/VIDEOID
    const embedMatch = u.match(/youtube\.com\/embed\/([^?&/]+)/i);
    if (embedMatch?.[1]) return embedMatch[1];

    // https://www.youtube.com/shorts/VIDEOID
    const shortsMatch = u.match(/youtube\.com\/shorts\/([^?&/]+)/i);
    if (shortsMatch?.[1]) return shortsMatch[1];

    // https://www.youtube.com/watch?v=VIDEOID
    const vParamMatch = u.match(/[?&]v=([^&]+)/i);
    if (vParamMatch?.[1]) return vParamMatch[1];

    // https://youtu.be/VIDEOID
    const shortMatch = u.match(/youtu\.be\/([^?&/]+)/i);
    if (shortMatch?.[1]) return shortMatch[1];

    return null;
  }

  private getYouTubeThumb(url: string): string | null {
    const id = this.getYouTubeId(url);
    if (!id) return null;
    // Good default preview
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  private pickVideoUrl(p: any): string {
    return (
      p?.video_url ||
      p?.createomate_video ||
      p?.youtube_url ||
      p?.article_url ||
      p?.link ||
      p?.url ||
      ''
    );
  }

  getPostImageSrc(p: any): string {
    const tag = String(p?.tag || '').toLowerCase().trim();
    const platform = String(p?.social_platform_title || '').toLowerCase().trim();
    const candidateVideoUrl = this.pickVideoUrl(p);
    if (tag === 'youtube' || platform === 'youtube' || this.isYouTubeUrl(candidateVideoUrl)) {
      const ytThumb = this.isYouTubeUrl(candidateVideoUrl) ? this.getYouTubeThumb(candidateVideoUrl) : null;
      if (ytThumb) return ytThumb;
    }

    if (p?.image_url) return p.image_url;
    if (tag === 'fbvideo' || tag === 'igvideo') return this.videoPlaceholder;
    return this.naPlaceholder;
  }

  onMainImgError(ev: Event, p?: any): void {
    const img = ev.target as HTMLImageElement;
    if (!img) return;
    const fallback = this.getPostImageSrc({ ...(p || {}), image_url: '' });
    if (img.src.endsWith(this.naPlaceholder) || img.src.endsWith(this.videoPlaceholder)) return;
    img.src = fallback || this.naPlaceholder;
  }
  isScheduledForToday(dateInput?: string | Date): boolean {
    if (!dateInput) return false;

    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return false;

    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }

  createNewPost() {
    this.router.navigate(['/create-post']);
  }
  createBulkPost() {
    this.router.navigate(['/bulk-upload']);
  }

  /**
 * Save the latest 3 posts from the current.
 */
  /**
 * Save the latest 3 *published* posts from the current list.
 */
  private setLatestPosts(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.posts || !this.posts.length) {
      localStorage.removeItem(this.LATEST_POSTS_KEY);
      return;
    }

    const publishedPosts = this.posts.filter(p => this.isPublished(p));

    if (!publishedPosts.length) {
      localStorage.removeItem(this.LATEST_POSTS_KEY);
      return;
    }

    const latestThree = publishedPosts.slice(0, 3);

    try {
      localStorage.setItem(this.LATEST_POSTS_KEY, JSON.stringify(latestThree));
    } catch (e) {
      console.error('Failed to save latest posts to localStorage', e);
    }
  }



  trackById = (_: number, item: Post) => item.id;
}